import React, { useEffect, useRef, useState } from "react";
import Spreadsheet from "x-data-spreadsheet";
import "x-data-spreadsheet/dist/xspreadsheet.css";
import "./sheetsExcel.css";
import * as XLSX from "xlsx";
import { Save, Loader2, Undo2, Redo2, Bold, Italic, Underline, Strikethrough, AlignLeft, AlignCenter, AlignRight, ClipboardPaste, Copy, Scissors, WrapText, Combine, Grid3X3, Filter, Snowflake, Sigma, ArrowUpFromLine, ArrowDownFromLine, ArrowLeftFromLine, ArrowRightFromLine, Trash2, Printer, FileDown, PaintBucket, ArrowUpToLine, FoldVertical, ArrowDownToLine } from "lucide-react";
import { toast } from "sonner";
import { dapi as api } from "./deskApi";
import { queueIfOffline } from "@/lib/offlineQueue";
import { formatErr } from "@/lib/api";
import { stox, xtos } from "./sheetConvert";

const XL_GREEN = "#217346";
const FONTS = ["Calibri", "Arial", "Georgia", "Times New Roman", "Courier New", "Verdana"];
const FSIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 36];
const NUM_FORMATS = [["normal", "General"], ["number", "Number"], ["percent", "Percentage"], ["usd", "Currency $"], ["eur", "Currency €"], ["date", "Date"], ["time", "Time"], ["text", "Text"]];
const FORMULAS = ["SUM", "AVERAGE", "COUNT", "MAX", "MIN", "IF", "CONCAT"];

const colName = (n) => { let s = ""; n += 1; while (n > 0) { n -= 1; s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26); } return s; };

function RBtn({ onClick, title, children, testid, active = false }) {
  return (
    <button type="button" title={title} data-testid={testid}
      onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className={`p-1 rounded-sm border ${active ? "bg-[#c5e0d0] border-[#217346]" : "border-transparent hover:bg-[#c8c6c4]/50 hover:border-[#c8c6c4]"} text-slate-700`}>
      {children}
    </button>
  );
}

function Group({ label, children }) {
  return (
    <div className="flex flex-col justify-between px-2 border-r border-[#d1cfcd] shrink-0">
      <div className="flex items-center gap-0.5 flex-1">{children}</div>
      <div className="text-[10px] text-[#605e5c] text-center pt-0.5 pb-0.5">{label}</div>
    </div>
  );
}

const RIBBON_TABS = [["home", "Home"], ["insert", "Insert"], ["formulas", "Formulas"], ["data", "Data"], ["view", "View"]];

/** Microsoft Excel look-alike spreadsheet (formulas, formatting, sheets).
 * Saves as .csheet (JSON) into the file manager; opens uploaded .xlsx. */
export default function SheetsApp({ file = null }) {
  const hostRef = useRef(null);
  const sRef = useRef(null);
  const [name, setName] = useState("Book1");
  const [nodeId, setNodeId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("home");
  const [fileMenu, setFileMenu] = useState(false);
  const [sel, setSel] = useState({ ri: 0, ci: 0, text: "" });
  const [fx, setFx] = useState("");
  const [stats, setStats] = useState(null);
  const [autoSave, setAutoSave] = useState(() => localStorage.getItem("desk_autosave") === "1");
  const [savedAt, setSavedAt] = useState(null);
  const autoRef = useRef({ enabled: false, hasNode: false, t: null });
  const saveRef = useRef(null);

  useEffect(() => {
    const el = hostRef.current;
    const s = new Spreadsheet(el, {
      showToolbar: false,
      showGrid: true,
      view: {
        height: () => el.clientHeight,
        width: () => el.clientWidth,
      },
    });
    sRef.current = s;
    const readCell = (ri, ci) => {
      try { return s.sheet.data.getCell(ri, ci)?.text ?? ""; } catch { return ""; }
    };
    s.on("cell-selected", (cell, ri, ci) => {
      setSel({ ri, ci, text: cell?.text ?? "" });
      setFx(cell?.text ?? "");
      setStats(null);
    });
    s.on("cells-selected", (cell, { sri, sci, eri, eci }) => {
      setSel({ ri: sri, ci: sci, text: cell?.text ?? "", range: { sri, sci, eri, eci } });
      setFx(cell?.text ?? "");
      let sum = 0, count = 0, cells = 0;
      for (let r = sri; r <= eri; r += 1) {
        for (let c = sci; c <= eci; c += 1) {
          const t = readCell(r, c);
          if (t !== "") cells += 1;
          const n = Number(t);
          if (t !== "" && !Number.isNaN(n)) { sum += n; count += 1; }
        }
      }
      setStats(count ? { sum, avg: sum / count, count: cells } : cells ? { count: cells } : null);
    });
    s.on("cell-edited", (text) => setFx(text ?? ""));
    s.change(() => {
      const a = autoRef.current;
      if (!a.enabled || !a.hasNode) return;
      clearTimeout(a.t);
      a.t = setTimeout(() => saveRef.current?.(true), 1500);
    });
    const ro = new ResizeObserver(() => { try { s.reRender(); } catch { /* noop */ } });
    ro.observe(el);
    (async () => {
      if (!file) return;
      try {
        const r = await api.get(`/desktop/fs/${file.id}/download`, { responseType: "blob" });
        if (/\.xlsx?$/i.test(file.name)) {
          const wb = XLSX.read(await r.data.arrayBuffer(), { type: "array" });
          s.loadData(stox(wb));
          setName(file.name.replace(/\.xlsx?$/i, ""));
        } else {
          s.loadData(JSON.parse(await r.data.text()));
          setName(file.name.replace(/\.csheet$/i, ""));
          setNodeId(file.id);
        }
      } catch (e) {
        toast.error(formatErr(e.response?.data?.detail) || "Could not open this spreadsheet.");
      }
    })();
    return () => { ro.disconnect(); el.innerHTML = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const sheet = () => sRef.current?.sheet;
  const chg = (t, v) => { try { sheet()?.toolbar.change(t, v); } catch (e) { console.warn(e); } };
  const cellStyle = () => { try { return sheet().data.getSelectedCellStyle(); } catch { return {}; } };
  const toggleFont = (k) => { const st = cellStyle(); chg(`font-${k}`, !(st.font || {})[k]); };

  const clip = (op) => {
    try {
      const d = sheet().data;
      if (op === "copy") d.copy();
      else if (op === "cut") d.cut();
      else d.paste("all");
      sRef.current.reRender();
    } catch { /* noop */ }
  };

  const insDel = (type) => {
    try {
      const d = sheet().data;
      if (type.startsWith("insert")) d.insert(type.replace("insert-", ""));
      else d.delete(type.replace("delete-", ""));
      sRef.current.reRender();
    } catch (e) { console.warn(e); }
  };

  const commitFx = (advance = false) => {
    try {
      const sh = sheet();
      sh.data.setCellText(sel.ri, sel.ci, fx, "finished");
      sRef.current.reRender();
      if (advance) {
        const nri = sel.ri + 1;
        sh.selector.set(nri, sel.ci);
        const next = sh.data.getCell(nri, sel.ci);
        setSel({ ri: nri, ci: sel.ci, text: next?.text ?? "" });
        setFx(next?.text ?? "");
      } else {
        setSel((p) => ({ ...p, text: fx }));
      }
    } catch { /* noop */ }
  };

  const save = async (silent = false) => {
    setBusy(true);
    if (!silent) setFileMenu(false);
    try {
      const json = JSON.stringify(sRef.current.getData());
      const b64 = btoa(unescape(encodeURIComponent(json)));
      const payload = {
        name: `${name.trim() || "Book1"}.csheet`,
        data_b64: b64,
        content_type: "application/json",
        node_id: nodeId,
      };
      try {
        const { data } = await api.post("/desktop/office/save-doc", payload);
        if (!nodeId) setNodeId(data.id);
        if (data.name) setName(data.name.replace(/\.csheet$/i, ""));
        setSavedAt(Date.now());
        window.dispatchEvent(new CustomEvent("desk-fs-changed"));
        if (!silent) toast.success(data.folder ? `Saved "${data.name || name}" in File Manager → Documents.` : `Saved "${name}".`);
      } catch (e) {
        if (!silent && queueIfOffline(e, { portal: "desk", method: "post", path: "/desktop/office/save-doc", body: payload, label: `Save spreadsheet "${name}"` })) {
          setSavedAt(Date.now());
          return;
        }
        if (silent) return;
        toast.error(
          e.response?.data?.detail
            ? formatErr(e.response.data.detail)
            : e.response
              ? `Save failed (HTTP ${e.response.status}). Please try again.`
              : "Network error while saving — check your connection and try again."
        );
      }
    } finally {
      setBusy(false);
    }
  };
  saveRef.current = save;
  autoRef.current.enabled = autoSave;
  autoRef.current.hasNode = !!nodeId;

  const exportXlsx = () => {
    setFileMenu(false);
    try {
      XLSX.writeFile(xtos(sRef.current.getData()), `${name.trim() || "Book1"}.xlsx`);
    } catch (e) { toast.error("Excel export failed: " + e.message); }
  };

  const st = {}; // avoid recomputing style per render; buttons act on click
  void st;

  const fmt = (n) => (Math.round(n * 100) / 100).toLocaleString();

  return (
    <div className="h-full flex flex-col bg-[#f3f2f1]" data-testid="sheets-app">
      {/* Excel title bar */}
      <div className="flex items-center gap-1 px-2 h-9 text-white shrink-0" style={{ background: XL_GREEN }}>
        <div className="w-6 h-6 rounded-sm bg-white/20 grid place-items-center text-[13px] font-bold shrink-0">X</div>
        <label className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/15 hover:bg-white/25 cursor-pointer select-none text-[11px] shrink-0" title={nodeId ? "Save automatically after you stop typing" : "AutoSave turns on after the first save"} data-testid="sheets-autosave-toggle">
          <input type="checkbox" className="sr-only" checked={autoSave} onChange={(e) => { setAutoSave(e.target.checked); localStorage.setItem("desk_autosave", e.target.checked ? "1" : "0"); }} />
          <span className={`relative w-7 h-3.5 rounded-full transition-colors ${autoSave ? "bg-emerald-300" : "bg-white/40"}`}>
            <span className={`absolute top-[2px] w-2.5 h-2.5 rounded-full bg-white shadow transition-all ${autoSave ? "left-[16px]" : "left-[2px]"}`} />
          </span>
          AutoSave {autoSave ? "On" : "Off"}
        </label>
        <button type="button" title="Save" onClick={() => save()} disabled={busy} className="p-1 rounded hover:bg-white/20 disabled:opacity-50" data-testid="sheets-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        </button>
        <button type="button" title="Undo" onClick={() => chg("undo")} className="p-1 rounded hover:bg-white/20" data-testid="sheets-undo"><Undo2 className="w-4 h-4" /></button>
        <button type="button" title="Redo" onClick={() => chg("redo")} className="p-1 rounded hover:bg-white/20" data-testid="sheets-redo"><Redo2 className="w-4 h-4" /></button>
        <div className="flex-1 flex items-center justify-center gap-1.5 min-w-0">
          <input value={name} onChange={(e) => setName(e.target.value)}
            className="bg-transparent text-white text-[13px] font-medium text-right outline-none border-b border-transparent focus:border-white/60 max-w-[45%] min-w-0"
            style={{ width: `${Math.max(5, name.length)}ch` }}
            data-testid="sheets-name-input" />
          <span className="text-[13px] text-white/85 whitespace-nowrap">- Excel</span>
        </div>
        <div className="w-24" />
      </div>

      {/* Ribbon tab strip */}
      <div className="flex items-end gap-0.5 px-1.5 pt-1 text-[13px] text-white shrink-0 relative" style={{ background: XL_GREEN }}>
        <button type="button" onClick={() => setFileMenu((v) => !v)}
          className="px-4 py-1 font-semibold hover:bg-white/15 rounded-t" data-testid="sheets-tab-file">File</button>
        {RIBBON_TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => { setTab(k); setFileMenu(false); }}
            className={`px-3.5 py-1 rounded-t ${tab === k && !fileMenu ? "bg-[#f3f2f1] text-[#217346] font-semibold" : "hover:bg-white/15"}`}
            data-testid={`sheets-tab-${k}`}>{l}</button>
        ))}
        {fileMenu && (
          <div className="absolute left-1.5 top-full z-30 bg-white shadow-2xl border border-slate-200 rounded-b-md w-64 py-1 text-slate-800" data-testid="sheets-file-menu">
            <button type="button" onClick={() => save()} className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] hover:bg-slate-100"><Save className="w-4 h-4 text-[#217346]" /> Save to File Manager</button>
            <button type="button" onClick={exportXlsx} className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] hover:bg-slate-100" data-testid="sheets-export-xlsx"><FileDown className="w-4 h-4 text-[#217346]" /> Download as Excel (.xlsx)</button>
            <button type="button" onClick={() => { setFileMenu(false); chg("print"); }} className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] hover:bg-slate-100" data-testid="sheets-print"><Printer className="w-4 h-4 text-[#217346]" /> Print</button>
          </div>
        )}
      </div>

      {/* Ribbon */}
      <div className="flex items-stretch bg-[#f3f2f1] border-b border-[#d1cfcd] px-1 py-1 overflow-x-auto shrink-0 min-h-[84px]">
        {tab === "home" && (
          <>
            <Group label="Clipboard">
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => clip("paste")}
                className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-sm hover:bg-[#c8c6c4]/50 text-slate-700" data-testid="sheets-paste">
                <ClipboardPaste className="w-6 h-6" /><span className="text-[10px]">Paste</span>
              </button>
              <div className="flex flex-col gap-0.5">
                <RBtn title="Cut" onClick={() => clip("cut")} testid="sheets-cut"><Scissors className="w-3.5 h-3.5" /></RBtn>
                <RBtn title="Copy" onClick={() => clip("copy")} testid="sheets-copy"><Copy className="w-3.5 h-3.5" /></RBtn>
              </div>
            </Group>
            <Group label="Font">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1">
                  <select defaultValue="Calibri" onChange={(e) => chg("font-name", e.target.value)} className="text-xs border border-[#c8c6c4] rounded-sm px-1 py-0.5 w-28 bg-white" data-testid="sheets-font">
                    {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <select defaultValue="10" onChange={(e) => chg("font-size", +e.target.value)} className="text-xs border border-[#c8c6c4] rounded-sm px-1 py-0.5 bg-white" data-testid="sheets-size">
                    {FSIZES.map((z) => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
                <div className="flex items-center">
                  <RBtn title="Bold" onClick={() => toggleFont("bold")} testid="sheets-bold"><Bold className="w-4 h-4" /></RBtn>
                  <RBtn title="Italic" onClick={() => toggleFont("italic")} testid="sheets-italic"><Italic className="w-4 h-4" /></RBtn>
                  <RBtn title="Underline" onClick={() => { const s2 = cellStyle(); chg("underline", !s2.underline); }} testid="sheets-underline"><Underline className="w-4 h-4" /></RBtn>
                  <RBtn title="Strikethrough" onClick={() => { const s2 = cellStyle(); chg("strike", !s2.strike); }} testid="sheets-strike"><Strikethrough className="w-4 h-4" /></RBtn>
                  <RBtn title="All borders" onClick={() => chg("border", { mode: "all", style: "thin", color: "#000000" })} testid="sheets-borders"><Grid3X3 className="w-4 h-4" /></RBtn>
                  <label className="p-1 cursor-pointer flex flex-col items-center" title="Fill color">
                    <PaintBucket className="w-3.5 h-3.5 text-slate-700" />
                    <input type="color" defaultValue="#ffff00" className="w-4 h-2 border-0 p-0 bg-transparent cursor-pointer" onChange={(e) => chg("bgcolor", e.target.value)} data-testid="sheets-fill" />
                  </label>
                  <label className="p-1 cursor-pointer flex flex-col items-center" title="Font color">
                    <span className="text-[11px] font-bold leading-none text-slate-700">A</span>
                    <input type="color" defaultValue="#c00000" className="w-4 h-2 border-0 p-0 bg-transparent cursor-pointer" onChange={(e) => chg("color", e.target.value)} data-testid="sheets-color" />
                  </label>
                </div>
              </div>
            </Group>
            <Group label="Alignment">
              <div className="flex flex-col gap-1">
                <div className="flex items-center">
                  <RBtn title="Top align" onClick={() => chg("valign", "top")} testid="sheets-vtop"><ArrowUpToLine className="w-4 h-4" /></RBtn>
                  <RBtn title="Middle align" onClick={() => chg("valign", "middle")} testid="sheets-vmid"><FoldVertical className="w-4 h-4" /></RBtn>
                  <RBtn title="Bottom align" onClick={() => chg("valign", "bottom")} testid="sheets-vbot"><ArrowDownToLine className="w-4 h-4" /></RBtn>
                  <RBtn title="Wrap text" onClick={() => { const s2 = cellStyle(); chg("textwrap", !s2.textwrap); }} testid="sheets-wrap"><WrapText className="w-4 h-4" /></RBtn>
                </div>
                <div className="flex items-center">
                  <RBtn title="Align left" onClick={() => chg("align", "left")} testid="sheets-left"><AlignLeft className="w-4 h-4" /></RBtn>
                  <RBtn title="Center" onClick={() => chg("align", "center")} testid="sheets-center"><AlignCenter className="w-4 h-4" /></RBtn>
                  <RBtn title="Align right" onClick={() => chg("align", "right")} testid="sheets-right"><AlignRight className="w-4 h-4" /></RBtn>
                  <RBtn title="Merge & center" onClick={() => { try { chg("merge", !sheet().data.canUnmerge()); } catch { /* noop */ } }} testid="sheets-merge"><Combine className="w-4 h-4" /></RBtn>
                </div>
              </div>
            </Group>
            <Group label="Number">
              <div className="flex flex-col gap-1 items-center">
                <select defaultValue="normal" onChange={(e) => chg("format", e.target.value)} className="text-xs border border-[#c8c6c4] rounded-sm px-1 py-0.5 w-28 bg-white" data-testid="sheets-format">
                  {NUM_FORMATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <div className="flex items-center gap-0.5">
                  <RBtn title="Currency" onClick={() => chg("format", "usd")} testid="sheets-fmt-usd"><span className="text-[13px] font-semibold w-4 inline-block text-center">$</span></RBtn>
                  <RBtn title="Percent" onClick={() => chg("format", "percent")} testid="sheets-fmt-pct"><span className="text-[13px] font-semibold w-4 inline-block text-center">%</span></RBtn>
                  <RBtn title="Comma / Number" onClick={() => chg("format", "number")} testid="sheets-fmt-num"><span className="text-[13px] font-semibold w-4 inline-block text-center">,</span></RBtn>
                </div>
              </div>
            </Group>
            <Group label="Editing">
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => chg("formula", "SUM")}
                className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-sm hover:bg-[#c8c6c4]/50 text-slate-700" data-testid="sheets-autosum">
                <Sigma className="w-6 h-6" /><span className="text-[10px]">AutoSum</span>
              </button>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => chg("autofilter")}
                className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-sm hover:bg-[#c8c6c4]/50 text-slate-700" data-testid="sheets-filter">
                <Filter className="w-6 h-6" /><span className="text-[10px]">Filter</span>
              </button>
            </Group>
          </>
        )}
        {tab === "insert" && (
          <Group label="Cells">
            {[["insert-row", "Insert Row", ArrowDownFromLine], ["insert-column", "Insert Column", ArrowRightFromLine], ["delete-row", "Delete Row", ArrowUpFromLine], ["delete-column", "Delete Column", ArrowLeftFromLine]].map(([t, l, I]) => (
              <button key={t} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insDel(t)}
                className="flex flex-col items-center gap-0.5 px-2.5 py-1 rounded-sm hover:bg-[#c8c6c4]/50 text-slate-700" data-testid={`sheets-${t}`}>
                {t.startsWith("delete") ? <Trash2 className="w-6 h-6" /> : <I className="w-6 h-6" />}<span className="text-[10px]">{l}</span>
              </button>
            ))}
          </Group>
        )}
        {tab === "formulas" && (
          <Group label="Function Library">
            {FORMULAS.map((f) => (
              <button key={f} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => chg("formula", f)}
                className="px-3 py-2 mx-0.5 bg-white border border-[#c8c6c4] rounded-sm hover:border-[#217346] text-[12px] font-mono text-slate-700" data-testid={`sheets-fx-${f}`}>
                {f}
              </button>
            ))}
          </Group>
        )}
        {tab === "data" && (
          <Group label="Sort & Filter">
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => chg("autofilter")}
              className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-sm hover:bg-[#c8c6c4]/50 text-slate-700" data-testid="sheets-data-filter">
              <Filter className="w-6 h-6" /><span className="text-[10px]">Filter</span>
            </button>
          </Group>
        )}
        {tab === "view" && (
          <Group label="Window">
            <button type="button" onMouseDown={(e) => e.preventDefault()}
              onClick={() => { try { const fz = sheet().data.freeze; chg("freeze", !(fz[0] > 0 || fz[1] > 0)); } catch { /* noop */ } }}
              className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-sm hover:bg-[#c8c6c4]/50 text-slate-700" data-testid="sheets-freeze">
              <Snowflake className="w-6 h-6" /><span className="text-[10px]">Freeze Panes</span>
            </button>
          </Group>
        )}
      </div>

      {/* Formula bar */}
      <div className="flex items-center gap-1 px-1 py-0.5 bg-white border-b border-[#d1cfcd] shrink-0" onClick={() => setFileMenu(false)}>
        <div className="w-16 text-center text-[12px] border border-[#c8c6c4] rounded-sm py-0.5 bg-white text-slate-700" data-testid="sheets-namebox">
          {colName(sel.ci)}{sel.ri + 1}
        </div>
        <div className="px-1.5 text-[12px] italic text-[#605e5c] border-r border-[#d1cfcd]">fx</div>
        <input
          value={fx}
          onChange={(e) => setFx(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitFx(true); } }}
          onBlur={() => { if (fx !== sel.text) commitFx(false); }}
          className="flex-1 text-[13px] outline-none px-1 py-0.5 min-w-0"
          placeholder=""
          data-testid="sheets-formula-input"
        />
      </div>

      <div ref={hostRef} className="flex-1 min-h-0 xl-grid" data-testid="sheets-grid" />

      {/* Status bar */}
      <div className="flex items-center justify-between h-6 px-3 text-[11px] text-white shrink-0" style={{ background: XL_GREEN }}>
        <div className="flex items-center gap-4">
          <span>Ready</span>
          {savedAt && <span className="text-emerald-200" data-testid="sheets-saved-badge">Saved ✓</span>}
        </div>
        <div className="flex items-center gap-4" data-testid="sheets-selection-stats">
          {stats?.sum !== undefined && <span>Average: {fmt(stats.avg)}</span>}
          {stats?.count !== undefined && <span>Count: {stats.count}</span>}
          {stats?.sum !== undefined && <span>Sum: {fmt(stats.sum)}</span>}
        </div>
      </div>
    </div>
  );
}
